import React, { useEffect } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useLazyQuery } from '@apollo/react-hooks'
import qs from 'query-string'

import { sanitizeDom } from '../../../../utils'
import { useIsAuthenticated } from '../../../../hooks/use-is-authenticated'
import { useSubscriptionPayment } from '../../hooks/use-subscription-payment'
import { usePriceChangeAlertFlag } from '../../../../hooks/usePriceChangeAlert'
import { usePlanDetails } from '../../../shared/hooks/use-plan-details'
import { signUp } from '../../../../lib/analytics/events'
import { planType, paymentAlertMessage } from '../../../shared/subscription-constants'
import { SIGN_UP_SCREENS, ADD_VOUCHER_SUCCESSFUL_MESSAGES } from '../../../../constants'
import VOUCHER_WITH_SKU_QUERY from '../../../../../graphql/queries/voucher-with-sku.gql'
import FullScreenLoadingComponent from '../../../../components/loading/fullscreen-loader'
import PaymentPriceChangeAlert from '../../../shared/payment-price-change-alert/payment-price-change-alert'
import PlanDetails from '../../../../components/plan-details'
import Step from '../step'
import styles from '../sign-up.css'

/**
 * Payment screen displaying the supplied plan details and an Evergent iframe
 * for the user to enter their payment information
 */
const Payment = () => {
  const currentScreen = SIGN_UP_SCREENS.PAYMENT
  const history = useHistory()
  const location = useLocation()
  const isAuthenticated = useIsAuthenticated()
  const { getPlanSkuByType } = usePlanDetails()
  const {
    copy,
    detail,
    loading,
    iframeRef,
    iframeUrl,
    showVoucherMessage,
    isTvodPlan,
    isAnnuallyPlan,
    isStandardPlan
  } = useSubscriptionPayment()
  const showPriceChangeAlert = usePriceChangeAlertFlag()

  const planTypeToE2E = {
    annually: 'signup-annual-iframe',
    tvod: 'signup-tvod-iframe',
    monthly: 'signup-monthly-iframe'
  }

  let iframeE2EAttribute = planTypeToE2E.monthly
  if (isAnnuallyPlan) {
    iframeE2EAttribute = planTypeToE2E.annually
  } else if (isTvodPlan) {
    iframeE2EAttribute = planTypeToE2E.tvod
  }

  const { voucher, plan } = qs.parse(location.search)
  const isValidPlan = plan && Object.values(planType).includes(plan)

  const [
    validateVoucher,
    { loading: isValidatingVoucher, error: voucherError }
  ] = useLazyQuery(VOUCHER_WITH_SKU_QUERY)

  useEffect(() => {
    if (!isAuthenticated) {
      history.push('/')
    }

    // Redirect to plan intro page if the plan is missing or invalid
    if (!isValidPlan) {
      history.replace(SIGN_UP_SCREENS.PLAN_INTRO.path)
    }

    // Track payment page view
    signUp.stepViewed(currentScreen.datalayerStepSequence, currentScreen.datalayerStepLabel, plan)

    // Validate voucher if it exists
    if (voucher) {
      const planSku = getPlanSkuByType(plan)
      if (planSku) {
        validateVoucher({
          variables: {
            voucherCode: voucher,
            signupSku: planSku
          }
        })
      }
    }
  }, [isAuthenticated, isValidPlan, plan, voucher])

  // Handle voucher validation result
  useEffect(() => {
    if (!isValidatingVoucher && voucherError) {
      // Redirect to plan intro page if voucher is invalid for this plan
      history.replace(SIGN_UP_SCREENS.PLAN_INTRO.path)
    }
  }, [isValidatingVoucher, voucherError])

  return (
    <>
      <div className={styles.topContainer}>
        <Step
          current={currentScreen.step}
          total={currentScreen.totalSteps}
        />
        <div className={styles.header}>Set up your payment</div>
        <div
          className={styles.subHeader}
          dangerouslySetInnerHTML={{ __html: sanitizeDom(copy) }}
        />
      </div>
      <div className={styles.container}>
        {!loading && (
          <>
            {!isTvodPlan && (
              <PlanDetails detail={detail} showVoucherMessage={showVoucherMessage} />
            )}

            {showPriceChangeAlert && <PaymentPriceChangeAlert isStandardPlan={isStandardPlan} isAnnuallyPlan={isAnnuallyPlan} />}

            {showVoucherMessage && (
              <div className={styles.voucherConfirmMessage}>
                {ADD_VOUCHER_SUCCESSFUL_MESSAGES.confirmMsg}
              </div>
            )}

            <div className={styles.divider} />

            <iframe
              ref={iframeRef}
              src={iframeUrl}
              className={styles.iframe}
              data-e2e={iframeE2EAttribute}
            />

            {isTvodPlan && (
              <div className={styles.tvodOnholdInfo}>
                A $1 hold transaction is used to authenticate your account, and will be refunded automatically.
              </div>
            )}

            <div className={styles.alertMessage}>
              {paymentAlertMessage}
            </div>
          </>
        )}
        <FullScreenLoadingComponent pastDelay={loading} data-e2e="signup-loader-visible" />
      </div>
    </>
  )
}

export default Payment
